import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
//import './CustomerReturnsList.css';
import {
    Button,
    Card,
    CardBody,
    Col,
    Container,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalHeader,
    Row,
    Spinner
} from 'reactstrap';
import {
    FaCheckCircle,
    FaEdit,
    FaFilePdf,
    FaFilter,
    FaMoneyBillWave,
    FaPlus,
    FaSearch,
    FaTrash,
    FaUndo
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { debounce } from 'lodash';
import { customerReturnActions } from '../../sagas/customerReturnSlice';
import { formatPrice } from '../../helpers/utility';
import CustomerReturnForm from './CustomerReturnForm';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Select from "react-select";
import { clientActions } from '../../sagas/clientSlice';
import { productActions } from '../../sagas/productSlice';
import logo from "../../assets/images/logo.png";

const loadImageBase64 = (src) =>
    new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = img.width;
            canvas.height = img.height;
            canvas.getContext("2d").drawImage(img, 0, 0);
            resolve(canvas.toDataURL("image/png"));
        };
        img.onerror = () => resolve(null); // fail-safe
        img.src = src;
    });


const CustomerReturnsList = () => {
    const dispatch = useDispatch();
    const { customerReturns, loading } = useSelector((state) => state.customerReturns);
    const [showModal, setShowModal] = useState(false);
    const { t } = useTranslation();
    const { company } = useSelector((s) => s.auth);

    const [selectedReturn, setSelectedReturn] = useState(null);
    const now = new Date();
    const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    const pad = (n) => n.toString().padStart(2, '0');
    const formatDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const searchInputRef = useRef(null);
    const clients = useSelector(state => state.clients.clients);
    const unites = useSelector(status => status.products.unites); // Assuming you have a Redux store with unites

    // Filter states
    const [filters, setFilters] = useState({
        clientId: '',
        dateFrom: formatDate(todayDate),
        dateTo: formatDate(tomorrowDate),
        query: ''
    });

    const navigate = useNavigate();

    useEffect(() => {
        // Auto focus on mount
        searchInputRef.current?.focus();

        // Add keyboard shortcut
        const handleKeyDown = (e) => {
            if (e.key === 'F2') {
                e.preventDefault();
                searchInputRef.current?.focus();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [customerReturns, filters]);

    useEffect(() => {
        if (clients && clients.length == 0) {
            dispatch(clientActions.search({ query: "" }));
        }
        dispatch(productActions.findAllUnites());
    }, []);

    useEffect(() => {
        dispatch(customerReturnActions.list({
            ...filters,
        }));
    }, [filters.clientId, filters.dateFrom, filters.dateTo]);


    const debouncedSearch = () => {
        dispatch(customerReturnActions.list({
            ...filters,
        }));
    }

    const clearFilters = () => {
        setFilters({
            query: '',
            clientId: null,
            dateFrom: formatDate(todayDate),
            dateTo: formatDate(tomorrowDate)
        });
        debouncedSearch();
    };

    const clientOptions = clients?.map(client => ({
        value: client.id,
        label: client.typeClient === 'PARTICULIER'
            ? `${client.nom} ${client.prenom || ''} `
            : client.entreprise,
        chantiers: client.chantiers || []
    })) ?? [];

    const clientOptionsWithAll = [
        { value: null, label: t('common.all', 'Tous') },
        ...clientOptions
    ];


    const handleShowModal = () => {
        setShowModal(true);
    };

    const handleEdit = (returnItem) => {
        setSelectedReturn(returnItem);
        setShowModal(true);
    }

    const handleCloseModal = () => {
        setShowModal(false);
        if (selectedReturn) {
            setSelectedReturn(null);
        }

        const payload = {
            ...filters
        };
        dispatch(customerReturnActions.list(payload));
    };

    const fmt = (n) => {
        const x = Number(n || 0);
        const s = (Math.round((x + Number.EPSILON) * 100) / 100).toFixed(2);
        return s.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    };
    const safe = (v, fb = '-') => (v || v === 0 ? v : fb);
    const toDateFR = (d) => {
        try { return new Date(d).toLocaleDateString('fr-FR'); } catch { return '-'; }
    };

    const generateReturnReceipt = async (returnItem, t = (k) => k, opts = {}) => {

        const {
            devise = 'DH',
            open = true,
            fileName = `BON_RETOUR_${safe(returnItem?.numeroRetour, returnItem?.id)}.pdf`,
        } = opts;

        const logoBase64 = await loadImageBase64(logo);

        // ================= DATA =================
        const rawLines =
            returnItem?.lignes ||
            returnItem?.lignesRetour ||
            returnItem?.lines ||
            [];

        const rows = rawLines.map((ln) => {
            const code = ln.articleCode || '';
            const name = ln.articleNom || '';
            const unite = unites.find(u => u.code === ln.unite)?.libelle || '';
            const qte = Number(ln.quantiteRetournee || 0);
            const tva = Number(ln.tauxTVA ?? 0);
            const puTTC = Number(ln.prixUnitaireTTC || 0);
            const totalTTC = Number(ln.montantTTC || (qte * puTTC));
            return { code, name, qte, unite, tva, puTTC, totalTTC };
        });

        const totalTTC = Number(returnItem?.montantTTC || 0);

        // ================= DOC =================
        const doc = new jsPDF({ unit: 'pt', format: 'a4' });

        const primary = [6, 145, 43];
        const ink = [15, 23, 42];
        const subtle = [100, 116, 139];
        const pageWidth = doc.internal.pageSize.getWidth();

        // ================= HEADER (ROW 1) =================
        const headerY = 45;

        if (logoBase64) {
            doc.addImage(logoBase64, 'PNG', 40, headerY - 20, 100, 70);
        }

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(18);
        doc.setTextColor(...primary);
        doc.text('BON DE RETOUR', pageWidth - 40, 40, { align: 'right' });

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(...subtle);
        doc.text(`# ${safe(returnItem?.numeroRetour, returnItem?.id)}`, pageWidth - 40, 58, { align: 'right' });
        doc.text(toDateFR(returnItem?.dateOperation || new Date()), pageWidth - 40, 72, { align: 'right' });

        // ================= ROW 2 : COMPANY (LEFT) =================
        let leftY = 100;

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.setTextColor(...ink);
        doc.text(safe(company?.name, 'Entreprise'), 40, leftY);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(...subtle);

        leftY += 16;
        const companyLines = [
            safe(company?.address, ''),
            [company?.phone1, company?.phone2].filter(Boolean).length
                ? `Tél: ${[company.phone1, company.phone2].filter(Boolean).join(' / ')}`
                : '',
            safe(company?.email, ''),
        ].filter(Boolean);

        companyLines.forEach(line => {
            doc.text(String(line), 40, leftY);
            leftY += 14;
        });

        // ================= ROW 2 : CLIENT (RIGHT) =================
        let rightY = 100;
        const rightX = pageWidth / 2 + 20;

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.setTextColor(...ink);
        doc.text('Client', rightX, rightY);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.text(safe(returnItem?.clientNom, '-'), rightX, rightY + 18);

        // ================= MODE / STATUT =================
        const modeMap = {
            AVOIR: 'Avoir',
            REMBOURSEMENT: 'Remboursement',
            ECHANGE: 'Échange',
        };
        const statutMap = {
            BROUILLON: 'Brouillon',
            VALIDE: 'Validé',
            ANNULE: 'Annulé',
            COMPTABILISE: 'Comptabilisé',
        };

        doc.setTextColor(...subtle);
        doc.text(`Mode: ${modeMap[returnItem?.modeRemboursement] || '-'}`, 40, leftY + 10);
        doc.text(`Statut: ${statutMap[returnItem?.statut] || '-'}`, 40, leftY + 24);

        // ================= TABLE =================
        autoTable(doc, {
            startY: 200,
            head: [[
                'Code',
                'Désignation',
                'Qté',
                'Unité',
                'PU TTC',
                'TVA %',
                'Total TTC',
            ]],
            body: rows.map(r => [
                r.code,
                r.name,
                r.qte,
                r.unite,
                `${fmt(r.puTTC)} ${devise}`,
                `${Math.round(r.tva * 100)}%`,
                `${fmt(r.totalTTC)} ${devise}`,
            ]),
            styles: { fontSize: 9, cellPadding: 6 },
            headStyles: { fillColor: primary, textColor: 255, fontStyle: 'bold' },
            alternateRowStyles: { fillColor: [245, 247, 250] },
            theme: 'striped',
        });

        // ================= TOTAL =================
        const endY = doc.lastAutoTable.finalY || 200;

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.setTextColor(...ink);
        doc.text('Total TTC:', 380, endY + 30);
        doc.text(`${fmt(totalTTC)} ${devise}`, 540, endY + 30, { align: 'right' });

        // ================= FOOTER =================
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(...subtle);
        doc.text(safe(company?.name, 'Entreprise'), 40, 820);
        doc.text(`Document généré le ${toDateFR(new Date())}`, 540, 820, { align: 'right' });

        // ================= OUTPUT =================
        if (open) {
            const blobUrl = doc.output('bloburl');
            window.open(blobUrl, '_blank');
        } else {
            doc.save(fileName);
        }
    };


    const handleValidateReturn = (returnItem) => {
        Swal.fire({
            title: t('customerReturns.confirmValidate'),
            text: t('customerReturns.confirmValidateMessage'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('common.yes'),
            cancelButtonText: t('common.no'),
        }).then((result) => {
            if (!result.isConfirmed) return;
            let payload = {
                id: returnItem.id,
                onSuccess: () => {
                    Swal.fire({
                        icon: 'success',
                        title: t('common.updated'),
                        text: t('customerReturns.validatedSuccess'),
                    });
                    // Refresh current page
                    const refreshPayload = {
                        ...filters
                    };
                    dispatch(customerReturnActions.list(refreshPayload));
                },
            };
            dispatch(customerReturnActions.validate(payload));
        });
    }

    const handleDeleteReturn = (returnItem) => {
        Swal.fire({
            title: t('common.confirm'),
            text: t('customerReturns.confirmDelete'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('common.yes'),
            cancelButtonText: t('common.no'),
        }).then((result) => {
            if (!result.isConfirmed) return;
            let payload = {
                id: returnItem.id,
                onSuccess: () => {
                    Swal.fire({
                        icon: 'success',
                        title: t('common.deleted'),
                        text: t('customerReturn.deletedSuccess'),
                    });
                    // Refresh current page
                    const refreshPayload = {
                        ...filters
                    };
                    dispatch(customerReturnActions.list(refreshPayload));
                },
            };
            dispatch(customerReturnActions.delete(payload));
        });
    }

    return (
        <Container fluid>
            <Row className="mb-4">
                <Col>
                    <Card className="shadow-sm">
                        <CardBody className="d-flex justify-content-between align-items-center">
                            <h4 className="mb-0 d-flex align-items-center">
                                <FaUndo className="text-primary me-2" />
                                {t('customerReturns.list')}
                            </h4>
                            <div className="d-flex align-items-center">
                                <Button color="primary" onClick={handleShowModal}>
                                    <FaPlus className="me-2" /> {t('customerReturns.new')}
                                </Button>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <Card className="shadow-sm">
                <CardBody>
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner color="primary" />
                            <div className="mt-2 text-muted">{t('common.loading')}</div>
                        </div>
                    ) : (
                        <>
                            <div className="filter-container mb-4">
                                <Row className="mb-3 g-2 align-items-end">
                                    <Col lg={4} md={4} sm={6} xs={12}>
                                        <FormGroup className="mb-0">
                                            <Label className="mb-1">{t('reglement.search')}</Label>
                                            <div className="input-group">
                                                <span className="input-group-text bg-light">
                                                    <FaSearch />
                                                </span>
                                                <Input
                                                    type="text"
                                                    innerRef={searchInputRef}
                                                    placeholder={t('customerReturns.searchPlaceholder')}
                                                    value={filters.query}
                                                    onChange={e => {
                                                        const value = e.target.value.toUpperCase();
                                                        setFilters(prevFilters => ({
                                                            ...prevFilters,
                                                            query: value
                                                        }));
                                                    }}
                                                    className="text-uppercase"
                                                    style={{ textTransform: 'uppercase' }}
                                                    onKeyDown={e => {
                                                        if (e.key === 'Enter') {
                                                            debouncedSearch();
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </FormGroup>
                                    </Col>
                                    <Col md={3}>
                                        <FormGroup className="mb-0">
                                            <Label htmlFor="clientId">{t('bonLivraison.client')}:</Label>
                                            <Select
                                                id="clientId"
                                                options={clientOptionsWithAll}
                                                value={clientOptionsWithAll.find(option => option.value === filters.clientId)}
                                                onChange={(selectedOption) => {
                                                    setFilters(f => ({ ...f, clientId: selectedOption ? selectedOption.value : null }));
                                                }}
                                                placeholder={t('bonLivraison.selectClient')}
                                                isSearchable
                                                menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                                                styles={{
                                                    menuPortal: base => ({ ...base, zIndex: 9999 })
                                                }}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col lg={2} md={3} sm={6} xs={12}>
                                        <FormGroup className="mb-0">
                                            <Label for="dateFrom" className="mb-1">Date début</Label>
                                            <Input
                                                type="date"
                                                id="dateFrom"
                                                value={filters.dateFrom}
                                                onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value }))}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col lg={2} md={3} sm={6} xs={12}>
                                        <FormGroup className="mb-0">
                                            <Label for="dateTo" className="mb-1">Date fin</Label>
                                            <Input
                                                type="date"
                                                id="dateTo"
                                                value={filters.dateTo}
                                                onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value }))}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col lg={1} md={1} sm={6} xs={12} className="d-flex align-items-end">
                                        <Button
                                            color="secondary"
                                            onClick={clearFilters}
                                            title={t('common.clearFilters')}
                                            className="mb-3"
                                            style={{ minHeight: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                        >
                                            <FaFilter />
                                        </Button>
                                    </Col>
                                </Row>
                            </div>
                            <div className="table-responsive">
                                <table className="table table-hover table-striped table-bordered">
                                    <thead className="thead-light">
                                        <tr>
                                            <th className="align-middle text-center">
                                                {t('customerReturns.numero')}
                                            </th>
                                            <th className="align-middle text-center">
                                                {t('customerReturns.date')}
                                            </th>
                                            <th className="align-middle text-center">
                                                {t('delivery.commercial')}
                                            </th>
                                            <th className="align-middle text-center">
                                                {t('delivery.vendeur')}
                                            </th>
                                            <th className="align-middle text-center">
                                                {t('customerReturns.client')}
                                            </th>
                                            <th className="align-middle text-center">
                                                {t('customerReturns.refundMode')}
                                            </th>
                                            <th className="align-middle text-center">
                                                {t('customerReturns.montantTTC')}
                                            </th>
                                            <th className="align-middle text-center">
                                                {t('customerReturns.montantHT')}
                                            </th>
                                            <th className="align-middle text-center">
                                                {t('customerReturns.status')}
                                            </th>
                                            <th className="align-middle text-center">{t('common.actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {customerReturns.map((returnItem) => (
                                            <tr key={returnItem.id}>
                                                <td className="align-middle text-center">
                                                    {returnItem.numeroRetour}
                                                </td>
                                                <td className="align-middle text-center">
                                                    {new Date(returnItem.dateOperation).toLocaleDateString('fr-FR')}
                                                </td>

                                                <td className="align-middle text-center">
                                                    {returnItem.commercialNom || 'N/A'}
                                                </td>
                                                <td className="align-middle text-center">
                                                    {returnItem.createdBy || 'N/A'}
                                                </td>
                                                <td className="align-middle text-center">
                                                    {returnItem.clientNom || 'N/A'}
                                                </td>
                                                <td className="align-middle text-center">
                                                    {returnItem.modeRemboursement === 'AVOIR' && (
                                                        <span className="badge bg-primary">{t('customerReturns.avoir')}</span>
                                                    )}
                                                    {returnItem.modeRemboursement === 'REMBOURSEMENT' && (
                                                        <span className="badge bg-info">{t('customerReturns.remboursement')}</span>
                                                    )}
                                                    {returnItem.modeRemboursement === 'ECHANGE' && (
                                                        <span className="badge bg-dark">{t('customerReturns.echange')}</span>
                                                    )}
                                                </td>
                                                <td className="align-middle text-center">{formatPrice(returnItem.montantTTC, false)}</td>
                                                <td className="align-middle text-center">{formatPrice(returnItem.montantHT, false)}</td>
                                                <td className="align-middle text-center">
                                                    {returnItem.statut === 'BROUILLON' && (
                                                        <span className="badge bg-warning">{t('customerReturns.brouillon')}</span>
                                                    )}
                                                    {returnItem.statut === 'ANNULE' && (
                                                        <span className="badge bg-danger">{t('customerReturns.annule')}</span>
                                                    )}
                                                    {returnItem.statut === 'VALIDE' && (
                                                        <span className="badge bg-success">{t('customerReturns.valide')}</span>
                                                    )}
                                                </td>
                                                <td className="align-middle text-center">
                                                    <div className="btn-group">
                                                        <Button
                                                            color="secondary"
                                                            title={t('customerReturns.generatePdf')}
                                                            onClick={() => generateReturnReceipt(returnItem)}
                                                            className="me-1"
                                                            size="sm"
                                                        >
                                                            <FaFilePdf />
                                                        </Button>
                                                        {returnItem.statut === 'BROUILLON' && (
                                                            <Button
                                                                color="success"
                                                                title={t('customerReturns.validate')}
                                                                onClick={() => handleValidateReturn(returnItem)}
                                                                className="me-1"
                                                                size="sm"
                                                            >
                                                                <FaCheckCircle />
                                                            </Button>
                                                        )}

                                                        <Button
                                                            color="warning"
                                                            title={t('customerReturns.edit')}
                                                            onClick={() => handleEdit(returnItem)}
                                                            size="sm"
                                                            className="me-1"
                                                        >
                                                            <FaEdit />
                                                        </Button>

                                                        {returnItem.statut === 'BROUILLON' && (
                                                            <Button
                                                                color="danger"
                                                                title={t('customerReturns.delete')}
                                                                onClick={() => handleDeleteReturn(returnItem)}
                                                                className="me-1"
                                                                size="sm"
                                                            >
                                                                <FaTrash />
                                                            </Button>
                                                        )}

                                                        {returnItem.statut === 'COMPTABILISE' &&
                                                            returnItem.modeRemboursement === 'REMBOURSEMENT' &&
                                                            !returnItem.estRembourse && (
                                                                <Button
                                                                    color="info"
                                                                    title={t('customerReturns.processRefund')}
                                                                    onClick={() => navigate(`/ventes/remboursements/new?retourId=${returnItem.id}`)}
                                                                    className="me-1"
                                                                    size="sm"
                                                                >
                                                                    <FaMoneyBillWave />
                                                                </Button>
                                                            )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                        {
                                            customerReturns.length === 0 && (
                                                <tr>
                                                    <td colSpan="10" className="text-center py-4">
                                                        <div className="text-muted">
                                                            {
                                                                t('customerReturns.noReturns')
                                                            }
                                                        </div>
                                                    </td>
                                                </tr>
                                            )
                                        }
                                    </tbody>
                                    <tfoot>
                                        <tr className="bg-light fw-bold">
                                            <td colSpan="6" className="text-end">{t('common.totals')}</td>
                                            <td className="text-center">{formatPrice(customerReturns.reduce((sum, ret) => sum + (ret.montantTTC || 0), 0), false)}</td>
                                            <td className="text-center">{formatPrice(customerReturns.reduce((sum, ret) => sum + (ret.montantHT || 0), 0), false)}</td>
                                            <td colSpan="2"></td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>

                        </>
                    )}
                </CardBody>
            </Card>

            {/* Modal for Customer Return Form */}
            <Modal isOpen={showModal} toggle={handleCloseModal} size="xl"
                className="modal-fullscreen-lg-down"
                style={{ maxWidth: '95%', width: '95%', margin: '10px auto' }}
            >
                <ModalHeader toggle={handleCloseModal}>
                    {selectedReturn ? `${t('customerReturns.edit')} N° ${selectedReturn.numeroRetour}` : t('customerReturns.new')}
                </ModalHeader>
                <ModalBody>
                    <CustomerReturnForm customerReturn={selectedReturn} onClose={handleCloseModal} />
                </ModalBody>
            </Modal>
        </Container>
    );
};

export default CustomerReturnsList;

